import { cn } from '@/utils'

interface FilterTabsProps<T extends string> {
  tabs: Record<T, string>
  activeTab: T
  onTabChange: (tab: T) => void
  ariaLabel?: string
  fullWidth?: boolean
  tabClassName?: string
}

export function FilterTabs<T extends string>({
  tabs,
  activeTab,
  onTabChange,
  ariaLabel = 'Filters',
  fullWidth = false,
  tabClassName = '',
}: Readonly<FilterTabsProps<T>>) {
  return (
    <div
      className={cn(
        'inline-flex items-center justify-between',
        'rounded-[var(--radius-md)]',
        'border border-solid border-gray-200',
        'bg-gray-50',
        'gap-0.5',
        'overflow-hidden',
        fullWidth && 'w-full'
      )}
      role="tablist"
      aria-label={ariaLabel}
    >
      {Object.keys(tabs).map((key) => {
        const tab = tabs[key as T]
        const isActive = activeTab === key
        return (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onTabChange(key as T)}
            className={cn(
              'px-3 py-2',
              'text-xs font-semibold',
              'rounded-[var(--radius-md)]',
              'border border-transparent bg-transparent',
              'transition-colors duration-150',
              'focus:outline-none',
              'focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-0',
              fullWidth && 'flex-1',
              tabClassName,
              isActive
                ? [
                    'bg-white text-primary-900',
                    'border-solid border-gray-300',
                    'shadow-custom-xs',
                  ]
                : ['text-quaternary-500 hover:text-gray-900 hover:bg-[#F3F4F6]']
            )}
          >
            {tab}
          </button>
        )
      })}
    </div>
  )
}
